import { collection, doc, getDocs, addDoc, deleteDoc, serverTimestamp, query, orderBy, updateDoc, Timestamp } from 'firebase/firestore';
import { db } from './firebase'; // firebase.ts 에서 db 인스턴스를 가져옴
import type { Student, StudentFormData } from '../types/student';

export const studentService = {
  // 특정 양육 과정의 수강생 목록 가져오기
  async getStudentsForClass(classId: string): Promise<Student[]> {
    const studentsCollectionRef = collection(db, 'discipleship-classes', classId, 'students');
    const q = query(studentsCollectionRef, orderBy('createdAt', 'asc')); // 이름순 또는 등록순 정렬 가능
    
    try {
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => {
        const data = doc.data();
        return { 
          ...data, 
          id: doc.id,
          classId: classId 
        } as Student;
      });
    } catch (error) {
      console.error(`Error fetching students for class ${classId}:`, error);
      throw error; // 오류를 다시 던져 UI에서 처리할 수 있도록 함
    }
  },

  // 양육 과정에 수강생 추가
  async addStudentToClass(classId: string, studentData: StudentFormData): Promise<string> {
    const studentsCollectionRef = collection(db, 'discipleship-classes', classId, 'students');
    
    let createdAtValue;
    if (studentData.registrationDate) {
      const dateObj = new Date(studentData.registrationDate);
      // Ensure the date input is interpreted as local date, not UTC.
      // Add time component to avoid potential timezone shifts if only date is given.
      const localDate = new Date(dateObj.getUTCFullYear(), dateObj.getUTCMonth(), dateObj.getUTCDate());
      if (!isNaN(localDate.getTime())) {
        createdAtValue = Timestamp.fromDate(localDate);
      } else {
        console.warn("Invalid registration date provided, defaulting to current time.");
        createdAtValue = serverTimestamp();
      }
    } else {
      createdAtValue = serverTimestamp();
    }

    try {
      const docRef = await addDoc(studentsCollectionRef, {
        name: studentData.name,
        createdAt: createdAtValue,
        attendance: {}, // Initialize attendance as empty object
        // completionStatus는 기본적으로 undefined로 설정됨
      });
      return docRef.id;
    } catch (error) {
      console.error(`Error adding student to class ${classId}:`, error);
      throw error;
    }
  },

  // 양육 과정에서 수강생 삭제
  async removeStudentFromClass(classId: string, studentId: string): Promise<void> {
    const studentDocRef = doc(db, 'discipleship-classes', classId, 'students', studentId);
    try {
      await deleteDoc(studentDocRef);
    } catch (error) {
      console.error(`Error removing student ${studentId} from class ${classId}:`, error);
      throw error;
    }
  },

  // 특정 수강생의 주차별 출석 상태 업데이트
  async updateStudentAttendance(
    classId: string, 
    studentId: string, 
    weekNumber: number, 
    status: '출석' | '결석' | '지각'
  ): Promise<void> {
    const studentDocRef = doc(db, 'discipleship-classes', classId, 'students', studentId);
    const fieldToUpdate = `attendance.week${weekNumber}`;
    try {
      await updateDoc(studentDocRef, {
        [fieldToUpdate]: status
      });
      console.log(`Successfully updated attendance for student ${studentId}, week ${weekNumber} to ${status}`);
    } catch (error) {
      console.error(`Error updating attendance for student ${studentId}, week ${weekNumber}:`, error);
      throw error;
    }
  },

  // 특정 수강생의 수료 상태 업데이트
  async updateStudentCompletionStatus(
    classId: string,
    studentId: string,
    status: '수료' | '보류'
  ): Promise<void> {
    const studentDocRef = doc(db, 'discipleship-classes', classId, 'students', studentId);
    try {
      await updateDoc(studentDocRef, {
        completionStatus: status
      });
      console.log(`Successfully updated completion status for student ${studentId} to ${status}`);
    } catch (error) {
      console.error(`Error updating completion status for student ${studentId}:`, error);
      throw error;
    }
  },

  // 특정 수강생의 등록일시(createdAt) 업데이트
  async updateStudentRegistrationDate(
    classId: string,
    studentId: string,
    newRegistrationDate: string // YYYY-MM-DD format
  ): Promise<void> {
    const studentDocRef = doc(db, 'discipleship-classes', classId, 'students', studentId);
    try {
      const dateObj = new Date(newRegistrationDate);
      // Ensure the date input is interpreted as local date by using UTC methods to construct
      const localDate = new Date(dateObj.getUTCFullYear(), dateObj.getUTCMonth(), dateObj.getUTCDate());
      if (isNaN(localDate.getTime())) {
        throw new Error("Invalid date format provided for registration date.");
      }
      const newTimestamp = Timestamp.fromDate(localDate);
      await updateDoc(studentDocRef, {
        createdAt: newTimestamp
      });
      console.log(`Successfully updated registration date for student ${studentId} to ${newRegistrationDate}`);
    } catch (error) {
      console.error(`Error updating registration date for student ${studentId}:`, error);
      throw error;
    }
  }
};